// src/api/etfApi.ts

import type {
  ETFSearchResponse,
  AnalyzeEtfResponse,
  AnalyzePairResponse,
  AnalyzeScreenerResponse,
  AnalyzeScreenerRequest,
  ScreenerStrategy,
  ChartInterval,
  Form4ScreenerResponse,
  PerformanceStats,
  StrategyPerformance,
  ModelPerformance,
  RecommendationTrack,
  Position,
  ExitSignal,
} from "./myTypes";

// Use relative path when bundled, or environment variable, or default to localhost
function getApiBase(): string {
  const envUrl = import.meta.env.VITE_API_URL?.toString()?.trim();
  
  // If VITE_API_URL is set, use it (but ensure it has a protocol)
  if (envUrl) {
    // If it already has http:// or https://, use as is
    if (envUrl.startsWith("http://") || envUrl.startsWith("https://")) {
      return envUrl;
    }
    // If it's just a hostname/port, add http://
    if (envUrl.includes(":") || envUrl.includes("localhost") || envUrl.match(/^\d+\.\d+\.\d+\.\d+/)) {
      return `http://${envUrl}`;
    }
    // Otherwise assume it's a full URL and return as is
    return envUrl;
  }
  
  // In development, talk directly to backend so /api and /screener etc. all work
  if (import.meta.env.DEV) {
    return "http://localhost:8000";
  }
  // Production: relative path (same origin) or set VITE_API_URL to backend URL
  return "";
}

const API_BASE = getApiBase();

// Helper function to construct API URLs properly
export function buildApiUrl(endpoint: string): string {
  // Remove leading slash from endpoint if present
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
  
  // If API_BASE is empty (relative path), return /endpoint
  if (!API_BASE || API_BASE === "") {
    return `/${cleanEndpoint}`;
  }
  
  // Remove trailing slash from API_BASE if present
  const cleanBase = API_BASE.endsWith("/") ? API_BASE.slice(0, -1) : API_BASE;
  
  // Return base/endpoint
  return `${cleanBase}/${cleanEndpoint}`;
}

async function handleJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `API error ${res.status}: ${
        text || res.statusText || "Unknown error from server"
      }`
    );
  }
  return res.json() as Promise<T>;
}

// Wrapper for fetch that handles network errors
async function fetchWithErrorHandling(url: string, options?: RequestInit): Promise<Response> {
  // Validate and normalize URL before attempting fetch
  let normalizedUrl = url;
  
  // If URL doesn't start with http/https and doesn't start with /, it might be malformed
  if (url && !url.startsWith("/") && !url.startsWith("http://") && !url.startsWith("https://")) {
    console.error("[API] Invalid URL format detected:", url);
    console.error("[API] API_BASE is:", API_BASE);
    throw new Error(`Invalid URL format: ${url}. Please check your API configuration (VITE_API_URL).`);
  }

  try {
    const response = await fetch(normalizedUrl, options);
    return response;
  } catch (error: any) {
    // Handle network errors (connection refused, DNS failure, CORS, etc.)
    // Network errors from fetch() ALWAYS throw TypeError
    if (error instanceof TypeError) {
      const errorMsg = (error.message || String(error) || "").toLowerCase();
      const errorName = error.name || "";
      
      // Network errors from fetch have these characteristics:
      // 1. Always TypeError
      // 2. Message typically contains: "fetch", "network", "failed", "connection", "refused", "cors"
      // 3. Sometimes the message is empty or just "TypeError"
      const isNetworkError = 
        errorMsg.includes("fetch") ||
        errorMsg.includes("network") ||
        errorMsg.includes("failed") ||
        errorMsg.includes("connection") ||
        errorMsg.includes("refused") ||
        errorMsg.includes("cors") ||
        errorMsg.includes("load") ||
        errorMsg.includes("net::") ||
        errorName === "TypeError";
      
      if (isNetworkError) {
        const baseUrl = API_BASE || window.location.origin;
        const fullUrl = normalizedUrl.startsWith("http") ? normalizedUrl : `${baseUrl}${normalizedUrl}`;
        
        console.error("[API] Network error detected:", {
          url: normalizedUrl,
          fullUrl,
          baseUrl,
          errorMessage: error.message,
          errorName: error.name
        });
        
        throw new Error(
          `Connection failed. Unable to reach the backend server at ${fullUrl}. ` +
          `Please ensure the backend server is running and accessible. ` +
          `If the problem persists, check your internet connection or VPN settings.`
        );
      }
    }
    
    // Re-throw any other errors as-is
    throw error;
  }
}

export async function searchEtfs(query: string): Promise<ETFSearchResponse> {
  const trimmed = query.trim();
  if (!trimmed) {
    return { query: "", total: 0, results: [] };
  }

  const url = buildApiUrl(`etfs/search?q=${encodeURIComponent(trimmed)}`);
  const res = await fetchWithErrorHandling(url);
  return handleJson<ETFSearchResponse>(res);
}

export type AnalysisMode = "standard" | "deep" | "quick";

//
// ------------------------------
// analyzeEtf (UPDATED FOR v1.2)
// ------------------------------
// Supports:
//   • analysisModel (classic | multi_turn_debate)
//   • recommendationLevel (Aggressive | Moderate | Conservative)
//
export async function analyzeEtf(params: {
  ticker: string;
  timeHorizonDays: number;
  analysisMode: AnalysisMode;
  analysisModel: string;          // NEW
  recommendationLevel: string;    // NEW
  language: string;                // NEW
  positionEntryPrice?: number;     // NEW: Optional position entry price (for soxl_tactical_10m)
  positionShares?: number;         // NEW: Optional position shares (for soxl_tactical_10m)
}): Promise<AnalyzeEtfResponse> {
  const body: any = {
    ticker: params.ticker.toUpperCase(),
    time_horizon_days: params.timeHorizonDays,
    analysis_mode: params.analysisMode,

    // NEW for ETF Brain 1.2 API
    analysis_model: params.analysisModel,
    recommendation_level: params.recommendationLevel,
    language: params.language,
  };

  // Add position tracking fields if provided (for SOXL tactical strategy)
  if (params.positionEntryPrice !== undefined && params.positionEntryPrice !== null) {
    body.position_entry_price = params.positionEntryPrice;
  }
  if (params.positionShares !== undefined && params.positionShares !== null) {
    body.position_shares = params.positionShares;
  }

  // Add cache-busting and timestamp to ensure fresh data
  const timestamp = Date.now();
  console.log(`[analyzeEtf] Making API call at ${new Date(timestamp).toISOString()} for ticker ${body.ticker}, model ${body.analysis_model}`);
  
  const res = await fetchWithErrorHandling(buildApiUrl("analyze_etf"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache, no-store, must-revalidate",
      "Pragma": "no-cache",
      "Expires": "0",
    },
    body: JSON.stringify(body),
  });
  
  console.log(`[analyzeEtf] API response received at ${new Date().toISOString()}`);

  return handleJson<AnalyzeEtfResponse>(res);
}

//
// ------------------------------
// analyzePair (UPDATED FOR v1.2)
// ------------------------------
// Supports:
//   • analysisModel (classic | multi_turn_debate)
//   • recommendationLevel (Aggressive | Moderate | Conservative)
//
export async function analyzePair(params: {
  primaryTicker: string;
  secondaryTicker: string;
  timeHorizonDays: number;
  analysisMode: AnalysisMode;
  analysisModel: string;        // NEW
  recommendationLevel: string;   // NEW
  language: string;              // NEW
}): Promise<AnalyzePairResponse> {
  const body = {
    primary_ticker: params.primaryTicker.toUpperCase(),
    secondary_ticker: params.secondaryTicker.toUpperCase(),
    time_horizon_days: params.timeHorizonDays,
    analysis_mode: params.analysisMode,

    // NEW for ETF Brain 1.2 API
    analysis_model: params.analysisModel,
    recommendation_level: params.recommendationLevel,
    language: params.language,
  };

  const res = await fetchWithErrorHandling(buildApiUrl("analyze_pair"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  return handleJson<AnalyzePairResponse>(res);
}

//
// ------------------------------
// Screener Functions
// ------------------------------

const SCREENER_STRATEGIES_TIMEOUT_MS = 15000;

async function fetchScreenerStrategiesOnce(url: string, signal: AbortSignal): Promise<ScreenerStrategy[]> {
  const res = await fetchWithErrorHandling(url, { signal });
  const data = await handleJson<{ strategies?: ScreenerStrategy[] }>(res);
  const list = data?.strategies;
  return Array.isArray(list) ? list : [];
}

export async function getScreenerStrategies(): Promise<ScreenerStrategy[]> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SCREENER_STRATEGIES_TIMEOUT_MS);
  const urlsToTry = [
    buildApiUrl("api/screener/strategies"),
    buildApiUrl("screener/strategies"),
  ];
  let lastError: unknown = null;
  for (const url of urlsToTry) {
    try {
      const strategies = await fetchScreenerStrategiesOnce(url, controller.signal);
      clearTimeout(timeoutId);
      return strategies;
    } catch (e: unknown) {
      lastError = e;
      if (e instanceof Error && e.name === "AbortError") {
        break;
      }
      // Try next URL on 404 or network error
    }
  }
  clearTimeout(timeoutId);
  if (lastError instanceof Error && lastError.name === "AbortError") {
    throw new Error("Request timed out. Is the backend running at " + (typeof window !== "undefined" ? "the API URL?" : "?") + " Try Retry or refresh.");
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}

export async function analyzeScreener(params: {
  ticker: string;
  screener: string;
  userPrompt?: string;
  chartInterval: ChartInterval;
  recommendationLevel: string;
  language: string;
}): Promise<AnalyzeScreenerResponse> {
  const body: AnalyzeScreenerRequest = {
    ticker: params.ticker.toUpperCase(),
    screener: params.screener,
    user_prompt: params.userPrompt,
    chart_interval: params.chartInterval,
    recommendation_level: params.recommendationLevel,
    language: params.language,
  };

  const res = await fetchWithErrorHandling(buildApiUrl("analyze_screener"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  return handleJson<AnalyzeScreenerResponse>(res);
}

export interface Form4ScreenerParams {
  days_recent?: number;
  days_aggregate?: number;
  max_tickers?: number;
  max_price?: number;
  only_open_market_buys?: boolean;
  include_sells?: boolean;
}

export async function getForm4Insiders(params: Form4ScreenerParams = {}): Promise<Form4ScreenerResponse> {
  const sp = new URLSearchParams();
  if (params.days_recent != null) sp.set("days_recent", String(params.days_recent));
  if (params.days_aggregate != null) sp.set("days_aggregate", String(params.days_aggregate));
  if (params.max_tickers != null) sp.set("max_tickers", String(params.max_tickers));
  if (params.max_price != null) sp.set("max_price", String(params.max_price));
  if (params.only_open_market_buys != null) sp.set("only_open_market_buys", String(params.only_open_market_buys));
  if (params.include_sells != null) sp.set("include_sells", String(params.include_sells));
  const url = `${buildApiUrl("api/screener/form4_insiders")}?${sp.toString()}`;
  const res = await fetchWithErrorHandling(url);
  return handleJson<Form4ScreenerResponse>(res);
}

// Shutdown function for executable mode
export async function shutdownApp(): Promise<void> {
  try {
    const res = await fetchWithErrorHandling(buildApiUrl("shutdown"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) {
      // If not in executable mode, endpoint will return 403
      throw new Error("Shutdown not available");
    }
    // Give a moment for the request to complete before closing
    setTimeout(() => {
      window.close();
    }, 500);
  } catch (error) {
    // Silently fail if not in executable mode
    console.log("Shutdown not available (not running as executable)");
  }
}

// ------------------------------
// Performance Tracking Functions
// ------------------------------

export async function trackRecommendation(params: {
  recommendationId: string;
  ticker: string;
  strategy: string;
  model: string;
  entryPrice?: number;
  recommendationData?: any;
  marketRegime?: any;
}): Promise<{ status: string; message: string }> {
  const body = {
    recommendation_id: params.recommendationId,
    ticker: params.ticker,
    strategy: params.strategy,
    model: params.model,
    entry_price: params.entryPrice,
    recommendation_data: params.recommendationData,
    market_regime: params.marketRegime,
  };

  const res = await fetchWithErrorHandling(buildApiUrl("api/track-recommendation"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  return handleJson<{ status: string; message: string }>(res);
}

export async function updateOutcome(params: {
  recommendationId: string;
  exitPrice: number;
}): Promise<{ status: string; message: string }> {
  const body = {
    recommendation_id: params.recommendationId,
    exit_price: params.exitPrice,
  };

  const res = await fetchWithErrorHandling(buildApiUrl("api/update-outcome"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  return handleJson<{ status: string; message: string }>(res);
}

export async function getPerformanceStats(params?: {
  strategy?: string;
  model?: string;
  minSamples?: number;
}): Promise<PerformanceStats> {
  const queryParams = new URLSearchParams();
  if (params?.strategy) queryParams.append("strategy", params.strategy);
  if (params?.model) queryParams.append("model", params.model);
  if (params?.minSamples) queryParams.append("min_samples", params.minSamples.toString());

  const endpoint = `api/performance-stats${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
  const res = await fetchWithErrorHandling(buildApiUrl(endpoint));
  return handleJson<PerformanceStats>(res);
}

export async function getStrategyPerformance(): Promise<StrategyPerformance> {
  const res = await fetchWithErrorHandling(buildApiUrl("api/strategy-performance"));
  return handleJson<StrategyPerformance>(res);
}

export async function getModelPerformance(): Promise<ModelPerformance> {
  const res = await fetchWithErrorHandling(buildApiUrl("api/model-performance"));
  return handleJson<ModelPerformance>(res);
}

export async function resetStats(): Promise<{ status: string; message: string }> {
  const res = await fetchWithErrorHandling(buildApiUrl("api/reset-stats"), {
    method: "POST",
  });
  return handleJson<{ status: string; message: string }>(res);
}

export async function getRecommendations(includeOpen: boolean = true): Promise<{ recommendations: RecommendationTrack[] }> {
  const res = await fetchWithErrorHandling(buildApiUrl(`api/recommendations?include_open=${includeOpen}`));
  return handleJson<{ recommendations: RecommendationTrack[] }>(res);
}

export async function deleteRecommendations(recommendationIds: string[]): Promise<{ status: string; message: string }> {
  const res = await fetchWithErrorHandling(buildApiUrl("api/recommendations/delete"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ recommendation_ids: recommendationIds }),
  });
  return handleJson<{ status: string; message: string }>(res);
}

// ------------------------------
// Position Tracking Functions
// ------------------------------

export async function deletePositions(positionIds: string[]): Promise<{ status: string; message: string }> {
  const res = await fetchWithErrorHandling(buildApiUrl("api/positions/delete"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ position_ids: positionIds }),
  });
  return handleJson<{ status: string; message: string }>(res);
}

export async function trackPosition(params: {
  positionId: string;
  recommendationId: string;
  ticker: string;
  entryPrice: number;
  stopLoss?: number;
  takeProfit?: number;
  positionSize?: number;
}): Promise<{ status: string; message: string }> {
  const body = {
    position_id: params.positionId,
    recommendation_id: params.recommendationId,
    ticker: params.ticker,
    entry_price: params.entryPrice,
    stop_loss: params.stopLoss,
    take_profit: params.takeProfit,
    position_size: params.positionSize,
  };

  const res = await fetchWithErrorHandling(buildApiUrl("api/track-position"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  return handleJson<{ status: string; message: string }>(res);
}

export async function getPositions(): Promise<{ positions: Position[] }> {
  const res = await fetchWithErrorHandling(buildApiUrl("api/positions"));
  return handleJson<{ positions: Position[] }>(res);
}

export async function closePosition(params: {
  positionId: string;
  exitPrice: number;
}): Promise<{ status: string; message: string }> {
  const body = {
    position_id: params.positionId,
    exit_price: params.exitPrice,
  };

  const res = await fetchWithErrorHandling(buildApiUrl("api/close-position"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  return handleJson<{ status: string; message: string }>(res);
}

export async function checkExits(): Promise<{ exit_signals: ExitSignal[] }> {
  const res = await fetchWithErrorHandling(buildApiUrl("api/check-exits"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  return handleJson<{ exit_signals: ExitSignal[] }>(res);
}

export async function getPendingActions(): Promise<{ positions: Position[]; count: number }> {
  const res = await fetchWithErrorHandling(buildApiUrl("api/positions/pending-actions"));
  return handleJson<{ positions: Position[]; count: number }>(res);
}

export async function triggerPositionAnalysis(positionId: string): Promise<{ status: string; analysis: any }> {
  const res = await fetchWithErrorHandling(buildApiUrl(`api/positions/${positionId}/trigger-analysis`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });
  return handleJson<{ status: string; analysis: any }>(res);
}

export interface DataProviderConfig {
  provider: string;
  polygon_available: boolean;
  polygon_configured: boolean;
}

export async function getDataProvider(): Promise<DataProviderConfig> {
  const url = buildApiUrl("api/data-provider");
  const res = await fetchWithErrorHandling(url);
  return handleJson<DataProviderConfig>(res);
}

// ------------------------------
// Authentication Functions
// ------------------------------

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  username: string;
  message: string;
}

export async function login(username: string, password: string): Promise<LoginResponse> {
  const body: LoginRequest = {
    username,
    password,
  };

  const res = await fetchWithErrorHandling(buildApiUrl("api/auth/login"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  return handleJson<LoginResponse>(res);
}

export async function verifyToken(token: string): Promise<{ valid: boolean; message: string }> {
  const url = buildApiUrl(`api/auth/verify?token=${encodeURIComponent(token)}`);
  const res = await fetchWithErrorHandling(url, {
    method: "POST",
  });
  return handleJson<{ valid: boolean; message: string }>(res);
}








